"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ALL_SERVICES } from "@/lib/services";
import { BookingModal } from "@/components/BookingModal";
import { ContactFooter } from "@/components/ContactFooter";

const CATEGORIES = [
  {
    label: "Skin Concerns",
    slugs: [
      "acne-treatment", "scar-treatment", "pigmentation-treatment",
      "melasma-treatment", "stretch-marks-treatment", "birthmark-removal",
      "hairfall-treatment", "underarms-lightening", "vaginal-lightening",
      "skin-tags-moles-removal", "skin-polishing", "tattoo-removal",
    ],
  },
  {
    label: "Laser & Facials",
    slugs: [
      "carbon-laser-facial", "laser-hair-removal", "laser-photo-facial",
      "hydrafacial", "hydrafacial-advanced", "oxygeneo-facial",
      "bikini-laser-hair-reduction", "underarms-laser-hair-reduction",
      "face-laser-hair-reduction", "male-intimate-laser-hair-reduction",
      "male-full-body-laser-hair-reduction", "permanent-beard-shaping",
      "permanent-moustache-shaping", "bridal-special-hydrafacial",
      "bridal-instant-glow-facial", "medifacials",
      "laser-vaginal-lightening", "dark-neck-laser-lightening",
    ],
  },
  {
    label: "Injectables & Aesthetics",
    slugs: [
      "prp-facial", "vampire-facial", "dermal-fillers", "botox",
      "mesotherapy", "skin-boosters", "exosomes-treatment",
      "lips-rejuvenation", "dark-circles-treatment",
    ],
  },
  {
    label: "Anti-Aging & Body",
    slugs: [
      "rf-skin-tightening", "anti-aging-treatment", "glutathione-therapy",
      "iv-infusion", "glutathione-iv-drip", "bridal-body-polishing",
      "led-light-therapy", "mnrf-treatment", "non-surgical-fat-reduction",
    ],
  },
  {
    label: "Permanent Makeup (PMU) Treatments",
    slugs: [
      "microblading", "powder-brows", "combo-brows", "nano-brows",
      "bb-glow-facial", "lip-blushing", "lip-neutralization",
      "full-lip-color-ombre-lips", "cosmetic-camouflage",
      "scar-vitiligo-camouflage", "scalp-micropigmentation",
      "areola-restoration",
    ],
  },
  {
    label: "Specialist Treatments",
    slugs: [
      "hair-transplant", "physiotherapy", "bridal-skincare", "rhinoplasty",
      "dental-treatments", "laser-hair-regrowth", "instant-glow-therapy",
      "advanced-skin-lightening", "skinsio-iv-drips-party",
    ],
  },
];

export default function ServicesPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <BookingModal open={showModal} onOpenChange={setShowModal} />

      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative py-24 px-4 text-center overflow-hidden">
          {/* Background image, very lightly blurred, fading to white at the bottom */}
          <div className="absolute inset-0">
            <Image
              src="/Services/services-hero.webp"
              alt="SkinSio Expert Medical Aesthetics Services - Vadodara Clinic"
              fill
              sizes="100vw"
              className="object-cover scale-110 blur-[2px]"
              priority
            />
            <div className="absolute inset-0 bg-background/35" />
            <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent via-background/70 to-background" />
          </div>

          {/* Foreground content */}
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="flex justify-center mb-6 mt-8">
              <Link href="/" aria-label="SkinSio Home">
                <div className="relative h-20 w-48">
                  <Image
                    src="/Logo.PNG"
                    alt="SkinSio Clinics Logo"
                    fill
                    sizes="192px"
                    className="object-contain"
                    priority
                  />
                </div>
              </Link>
            </div>
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
              SkinSio Clinics · Vadodara
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Treatments</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Advanced skin, hair, and aesthetic treatments delivered by expert
              Doctors &amp; Plastic Surgeons using US FDA-approved technology.
            </p>
          </div>
        </section>

        {/* Services by Category */}
        <section className="max-w-6xl mx-auto px-4 py-16 space-y-14">
          {CATEGORIES.map((cat) => {
            const services = cat.slugs
              .map((slug) => ALL_SERVICES.find((s) => s.slug === slug))
              .filter(Boolean);
            return (
              <div key={cat.label}>
                <h2 className="text-xl font-bold mb-6 pb-2 border-b border-border">
                  {cat.label}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service) => (
                    <Link
                      key={service!.slug}
                      href={`/services/${service!.slug}`}
                      className="group p-5 rounded-2xl border bg-card hover:border-primary/50 hover:shadow-md transition-all duration-200"
                    >
                      <h3 className="font-semibold text-base mb-2 group-hover:text-primary transition-colors">
                        {service!.name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {service!.description}
                      </p>
                      <span className="inline-block mt-3 text-sm text-primary font-medium group-hover:underline">
                        Learn more →
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        {/* CTA */}
        <section className="bg-primary/5 py-12 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">Not Sure Where to Start?</h2>
            <p className="text-muted-foreground mb-6">
              Book a consultation and our expert Doctors &amp; Plastic Surgeons
              will recommend the right treatment for your skin.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Book Consultation
            </button>
          </div>
        </section>

        {/* Contact Footer — map, hours, social links */}
        <ContactFooter />
      </main>
    </>
  );
}
